import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChartLineUp, Sun, Moon, List } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/reports", label: "Reports" },
  { to: "/billing", label: "Billing" },
  { to: "/settings", label: "Account Settings" },
];

export default function Navbar({ onToggleSidebar }) {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 inset-x-0 z-40 h-16 border-b border-border bg-background/95 backdrop-blur-md">
      <div className="h-full flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="lg:hidden p-2 rounded-md hover:bg-accent transition-colors"
            data-testid="sidebar-toggle-button"
          >
            <List className="h-5 w-5" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary/15 flex items-center justify-center">
              <ChartLineUp className="h-4.5 w-4.5 text-primary" weight="bold" />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight hidden sm:inline">AdCrew OS</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-6">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`navbar-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn(
                  "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  location.pathname === link.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="h-9 w-9 rounded-md flex items-center justify-center hover:bg-accent transition-colors"
            data-testid="theme-toggle-button"
            title="Toggle dark/light mode"
          >
            {theme === "dark" ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
          </button>

          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-1" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback>{user?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings-item">Account Settings</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing-item">Billing</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">Log Out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <nav className="lg:hidden flex items-center gap-1 px-4 pb-2 overflow-x-auto border-t border-border/50 pt-2">
        {NAV_LINKS.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            data-testid={`navbar-mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={cn(
              "px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors",
              location.pathname === link.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"
            )}
          >
            {link.label}
          </Link>
        ))}
      </nav>
    </header>
  );
}
